import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useProjects } from '@/hooks/useProjects'
import ProjectList from './ProjectList'
import { ProgressSpinner } from '../Common/ProgressSpinner'

export type ProjectSize = 'normal' | 'large'
export type ProjectType = 'Residence' | 'Commercial' | 'Apartments'
export type FilterType = 'All' | ProjectType

const filters: FilterType[] = ['All', 'Residence', 'Commercial', 'Apartments']

export interface Project {
  id: number
  image: string
  title: string
  location: string
  type: ProjectType
}

const Projects: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('All')
  const { projects, isLoading, isError } = useProjects()
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([])

  useEffect(() => {
    if (projects) {
      const filtered = projects.filter(
        (project) => activeFilter === 'All' || project.type === activeFilter,
      )
      setFilteredProjects(filtered)
    }
  }, [activeFilter, projects])

  return (
    <div className="max-w-7xl mx-auto px-4 py-20 my-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-6">
        <div>
          <p className="text-base font-semibold leading-7 text-primary">
            Quality projects delivered to standards
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Projects
            <span className="text-gray-400 ml-2">
              ({filteredProjects.length})
            </span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          {filters.map((filter) => (
            <motion.button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-6 py-2 rounded-full text-sm transition-colors relative ${
                activeFilter === filter
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {activeFilter === filter && (
                <motion.div
                  layoutId="activeFilter"
                  className="absolute inset-0 bg-primary rounded-full"
                  initial={false}
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-10">{filter}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <div className="text-center py-10">
          <p className="text-red-500">
            Error loading projects. Please try again later.
          </p>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <ProgressSpinner />
        </div>
      ) : (
        <ProjectList filteredProjects={filteredProjects} />
      )}
    </div>
  )
}

export default Projects
